import { useEffect, useRef, useState } from 'react';
import { searchTenants, getTenantSummary, type TenantSummary } from '@/lib/api';
import { subscriptionStatusLabel } from '@/components/badge-labels';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MIN_QUERY = 2;
const DEBOUNCE_MS = 300;

interface Props {
  value: TenantSummary | null;
  onChange: (tenant: TenantSummary | null) => void;
  /** When true, the selector is read-only (e.g. editing an existing override). */
  disabled?: boolean;
  id?: string;
}

/**
 * Async search-select for picking a tenant by name/slug (issue: feature-flag
 * PerTenant override should not require pasting a raw GUID). Calls
 * GET /platform/tenants?q=. Accepts a pasted UUID as a fallback, resolving the
 * tenant name when possible.
 */
export function TenantSelector({ value, onChange, disabled, id }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TenantSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);

  const pastedUuid = UUID_PATTERN.test(query.trim()) ? query.trim() : null;

  // Debounced search on query change.
  useEffect(() => {
    const term = query.trim();
    if (pastedUuid || term.length < MIN_QUERY) {
      // Limpar a lista quando o termo encolhe e sincronizacao com o input, nao
      // cascata de render: sem isso o resultado anterior fica na tela contra-
      // dizendo o que esta digitado.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setResults([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      searchTenants(term)
        .then((items) => { if (!cancelled) { setResults(items); setActive(0); } })
        .catch(() => { if (!cancelled) setResults([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, pastedUuid]);

  // Close dropdown on outside click.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function select(t: TenantSummary) {
    onChange(t);
    setQuery('');
    setResults([]);
    setOpen(false);
  }

  async function applyPastedUuid(uuid: string) {
    const resolved = await getTenantSummary(uuid);
    // Fall back to an id-only summary so the override can still be saved by GUID.
    select(resolved ?? {
      id: uuid, tradeName: '(loja não encontrada)', ownerName: '',
      slug: '', phone: '', isActive: false, subscriptionStatus: '',
    });
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (pastedUuid && e.key === 'Enter') { e.preventDefault(); void applyPastedUuid(pastedUuid); return; }
    if (!results.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); select(results[active]); }
  }

  // Selected state — show the chosen tenant with a "trocar" button.
  if (value) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        padding: '8px 12px', background: 'var(--color-surface-alt)', borderRadius: 8,
      }}>
        <span>
          <strong>{value.tradeName || '(sem nome)'}</strong>
          {value.slug && <span className="muted"> · {value.slug}</span>}
          <span className="mono muted" style={{ display: 'block', fontSize: 11 }}>{value.id}</span>
        </span>
        {!disabled && (
          <button type="button" className="btn btn-secondary" onClick={() => onChange(null)}>
            Trocar
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls="tenant-selector-list"
        autoComplete="off"
        value={query}
        disabled={disabled}
        placeholder="Buscar loja por nome ou slug…"
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />

      {pastedUuid && (
        <button type="button" className="btn btn-secondary" style={{ marginTop: 8 }}
          onClick={() => void applyPastedUuid(pastedUuid)}>
          Usar ID colado: <span className="mono">{pastedUuid}</span>
        </button>
      )}

      {open && !pastedUuid && query.trim().length >= MIN_QUERY && (
        <ul id="tenant-selector-list" role="listbox" style={{
          listStyle: 'none', margin: '4px 0 0', padding: 4, position: 'absolute', zIndex: 50,
          left: 0, right: 0, background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8,
          boxShadow: 'var(--shadow-md)', maxHeight: 260, overflowY: 'auto',
        }}>
          {loading && <li className="muted" style={{ padding: 8 }}>Buscando…</li>}
          {!loading && results.length === 0 && (
            <li className="muted" style={{ padding: 8 }}>Nenhuma loja encontrada</li>
          )}
          {!loading && results.map((t, i) => (
            <li key={t.id} role="option" aria-selected={i === active}>
              <button type="button" onMouseEnter={() => setActive(i)} onClick={() => select(t)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px',
                  border: 'none', borderRadius: 6, cursor: 'pointer',
                  background: i === active ? 'var(--color-surface-alt)' : 'transparent',
                }}>
                <strong>{t.tradeName || '(sem nome)'}</strong>
                <span className="muted"> · {t.slug}</span>
                <span className="muted" style={{ display: 'block', fontSize: 12 }}>
                  {t.ownerName}{t.isActive ? '' : ' · inativo'} · {subscriptionStatusLabel(t.subscriptionStatus)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
